import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { request } from './api';

export interface BackofficeUser {
  id: string;
  email: string;
  full_name: string | null;
}

interface AuthContextValue {
  user: BackofficeUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BackofficeUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Al montar, recupera la sesión de la cookie bo_session (si sigue viva).
  useEffect(() => {
    request<BackofficeUser>('/backoffice/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const u = await request<BackofficeUser>('/backoffice/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(u);
  };

  const logout = async () => {
    await request('/backoffice/auth/logout', { method: 'POST' });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
